/**
 * Configuration Setting API Actions
 *
 * All API request actions for the Configuration Setting page:
 * - Programs CRUD (list, detail, create, update, status)
 * - Product Families (list, create)
 * - Category duplicate check
 * - Program options (cascading program †?CM †?location from Auto Report)
 */

import ConfigurationApiTypes from "../types/configurationApiTypes";
import { config } from "../../config/config";

//  Configuration: Programs 

/**
 * Retrieve all program configurations (list page)
 * GET /configuration/programs
 * Query params ?UserName=xxx&Email=xxx@amazon.com are auto-appended by saga
 */
export const getProgramsRequest = () => ({
  type: ConfigurationApiTypes.GET_PROGRAMS_REQUEST,
  http: {
    cognito: {
      effect: {
        url: `${(config as any).BASE_URL}/configuration/programs`,
        method: "GET",
      },
      commit: { type: ConfigurationApiTypes.GET_PROGRAMS_SUCCESS },
      rollback: { type: ConfigurationApiTypes.GET_PROGRAMS_FAILURE },
    },
  },
});

/**
 * Retrieve a single program's full configuration + target settings
 * GET /configuration/programs/<program_id>
 */
export const getProgramDetailRequest = (programId: string) => ({
  type: ConfigurationApiTypes.GET_PROGRAM_DETAIL_REQUEST,
  http: {
    cognito: {
      effect: {
        url: `${(config as any).BASE_URL}/configuration/programs/${programId}`,
        method: "GET",
      },
      commit: { type: ConfigurationApiTypes.GET_PROGRAM_DETAIL_SUCCESS },
      rollback: { type: ConfigurationApiTypes.GET_PROGRAM_DETAIL_FAILURE },
    },
  },
});

/**
 * Create (onboard) a new program with basic info + target settings
 * POST /configuration/programs
 * basic_info accepts BasicInfoFormData directly — extra fields are ignored by the API
 */
export const createProgramRequest = (payload: {
  basic_info: Record<string, any>;
  target_settings: Array<{
    category_subItem: string;
    target_value?: number;
    dri?: string;
  }>;
}) => ({
  type: ConfigurationApiTypes.CREATE_PROGRAM_REQUEST,
  http: {
    cognito: {
      effect: {
        url: `${(config as any).BASE_URL}/configuration/programs`,
        method: "POST",
        json: payload,
      },
      commit: { type: ConfigurationApiTypes.CREATE_PROGRAM_SUCCESS },
      rollback: { type: ConfigurationApiTypes.CREATE_PROGRAM_FAILURE },
    },
  },
});

/**
 * Update program's basic info + target settings (single or batch)
 * PUT /configuration/programs/update
 * Incremental update ?only modified fields are sent
 */
export const updateProgramsRequest = (payload: {
  program_ids: Array<{ program_id: string; version: number }>;
  basic_info?: Record<string, any>;
  target_settings?: Array<{
    category_subItem: string;
    target_value?: number;
    dri?: string;
  }>;
  delete_target_settings?: string[];
}) => ({
  type: ConfigurationApiTypes.UPDATE_PROGRAMS_REQUEST,
  http: {
    cognito: {
      effect: {
        url: `${(config as any).BASE_URL}/configuration/programs/update`,
        method: "PUT",
        json: payload,
      },
      commit: { type: ConfigurationApiTypes.UPDATE_PROGRAMS_SUCCESS },
      rollback: { type: ConfigurationApiTypes.UPDATE_PROGRAMS_FAILURE },
    },
  },
});

/**
 * Disable (soft delete) program (single or batch)
 * PUT /configuration/programs/status
 */
export const updateProgramsStatusRequest = (payload: {
  program_ids: Array<{ program_id: string; version: number }>;
  status: "enabled" | "disabled";
}) => ({
  type: ConfigurationApiTypes.UPDATE_PROGRAMS_STATUS_REQUEST,
  http: {
    cognito: {
      effect: {
        url: `${(config as any).BASE_URL}/configuration/programs/status`,
        method: "PUT",
        json: payload,
      },
      commit: { type: ConfigurationApiTypes.UPDATE_PROGRAMS_STATUS_SUCCESS },
      rollback: { type: ConfigurationApiTypes.UPDATE_PROGRAMS_STATUS_FAILURE },
    },
  },
});

//  Configuration: Product Families 

/**
 * Retrieve all active product families
 * GET /configuration/product-families
 */
export const getProductFamiliesRequest = () => ({
  type: ConfigurationApiTypes.GET_PRODUCT_FAMILIES_REQUEST,
  http: {
    cognito: {
      effect: {
        url: `${(config as any).BASE_URL}/configuration/product-families`,
        method: "GET",
      },
      commit: { type: ConfigurationApiTypes.GET_PRODUCT_FAMILIES_SUCCESS },
      rollback: { type: ConfigurationApiTypes.GET_PRODUCT_FAMILIES_FAILURE },
    },
  },
});

/**
 * Create a new product family
 * POST /configuration/product-families
 */
export const createProductFamilyRequest = (payload: {
  product_family_name: string;
  description?: string;
}) => ({
  type: ConfigurationApiTypes.CREATE_PRODUCT_FAMILY_REQUEST,
  http: {
    cognito: {
      effect: {
        url: `${(config as any).BASE_URL}/configuration/product-families`,
        method: "POST",
        json: payload,
      },
      commit: { type: ConfigurationApiTypes.CREATE_PRODUCT_FAMILY_SUCCESS },
      rollback: { type: ConfigurationApiTypes.CREATE_PRODUCT_FAMILY_FAILURE },
    },
  },
});

//  Configuration: Category Duplicate Check 

/**
 * Check duplicate category for a program
 * POST /configuration/programs/<program_id>/categories/check
 */
export const checkCategoryDuplicateRequest = (
  product_family_name: string
) => ({
  type: ConfigurationApiTypes.CHECK_CATEGORY_DUPLICATE_REQUEST,
  http: {
    cognito: {
      effect: {
        url: `${(config as any).BASE_URL}/configuration/programs/categories/check`,
        method: "POST",
        json: { product_family_name: product_family_name },
      },
      commit: { type: ConfigurationApiTypes.CHECK_CATEGORY_DUPLICATE_SUCCESS },
      rollback: { type: ConfigurationApiTypes.CHECK_CATEGORY_DUPLICATE_FAILURE },
    },
  },
});

//  Configuration: Program Options (from Auto Report) 

/**
 * Retrieve program/cm/location/operation options from Auto Report
 * GET /configuration/programs/options
 * Returns flat lists: { programs: [...], cms: [...], locations: [...], operations: [...] }
 */
export const getProgramOptionsRequest = () => ({
  type: ConfigurationApiTypes.GET_PROGRAM_OPTIONS_REQUEST,
  http: {
    cognito: {
      effect: {
        url: `${(config as any).BASE_URL}/configuration/programs/options`,
        method: "GET",
      },
      commit: { type: ConfigurationApiTypes.GET_PROGRAM_OPTIONS_SUCCESS },
      rollback: { type: ConfigurationApiTypes.GET_PROGRAM_OPTIONS_FAILURE },
    },
  },
});

//  Clear transient results/errors 

/**
 * Clear all transient result/error fields in configuration state.
 * Should be dispatched on component mount to prevent stale notifications
 * from being re-displayed when navigating back to the page.
 */
export const clearConfigurationResults = () => ({
  type: ConfigurationApiTypes.CLEAR_CONFIGURATION_RESULTS,
});
